import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { useLocation } from "wouter";
import { Button } from "../components/Button";
import { Card } from "../components/Card";
import { EmptyState } from "../components/EmptyState";
import { Field } from "../components/Field";
import { Skeleton } from "../components/Spinner";
import { useToast } from "../components/Toast";
import { ApiError, apiGet, apiSend } from "../lib/customFetch";
import { notifyFeaturesUpdated } from "../lib/useFeatures";
import styles from "./OnboardingPage.module.css";

type OnboardingStep =
  | "virksomhed"
  | "arbejdsform"
  | "regler"
  | "medarbejder"
  | "afsluttet";
type RegistreringsMode = "stempel" | "standard";
type ProfilKey = "haandvaerk" | "kontor" | "transport" | "butik";

interface Konfiguration {
  firmanavn: string;
  registreringsMode: RegistreringsMode;
  pause: { aktiv: boolean; taerskelMinutter: number; minutter: number };
  projekterAktiv: boolean;
  features: { clockin: boolean; ai: boolean; koersel: boolean };
}

interface Oversigt {
  state: { step: OnboardingStep; afsluttet: boolean };
  konfiguration: Konfiguration;
}

interface Profil extends Omit<Konfiguration, "firmanavn"> {
  key: ProfilKey;
  navn: string;
  beskrivelse: string;
}

interface Preview {
  type: "profil" | "manuel";
  profil: ProfilKey | null;
  konfiguration: Konfiguration;
}

interface Employee {
  id: string;
  navn: string;
}

interface Projekt {
  id: string;
  navn: string;
  kundenavn: string | null;
  aktiv: boolean;
}

interface InviteInfo {
  userId: string;
  navn: string;
  inviteUrl: string;
  qrDataUrl: string;
  udloeberAt: string;
}

type LoadState =
  | { status: "loading" }
  | { status: "forbidden" }
  | { status: "failed" }
  | {
      status: "ready";
      oversigt: Oversigt;
      profiler: Profil[];
      employees: Employee[];
    };

const STEP_ORDER: readonly OnboardingStep[] = [
  "virksomhed",
  "arbejdsform",
  "medarbejder",
  "afsluttet",
];

const STEP_LABELS: Record<OnboardingStep, string> = {
  virksomhed: "Virksomhed",
  arbejdsform: "Arbejdsform og regler",
  regler: "Arbejdsform og regler",
  medarbejder: "Første medarbejder",
  afsluttet: "Klar",
};

const PROJEKT_FETCH_TIMEOUT_MS = 8_000;

export function OnboardingPage() {
  const toast = useToast();
  const [, navigate] = useLocation();
  const [load, setLoad] = useState<LoadState>({ status: "loading" });
  const [invite, setInvite] = useState<InviteInfo | null>(null);

  const hent = useCallback(() => {
    setLoad({ status: "loading" });
    Promise.all([
      apiGet<Oversigt>("/api/onboarding"),
      apiGet<Profil[]>("/api/onboarding/profiler"),
      apiGet<Employee[]>("/api/employees"),
    ])
      .then(([oversigt, profiler, employees]) =>
        setLoad({ status: "ready", oversigt, profiler, employees }),
      )
      .catch((cause: unknown) =>
        setLoad(
          cause instanceof ApiError && cause.status === 403
            ? { status: "forbidden" }
            : { status: "failed" },
        ),
      );
  }, []);

  useEffect(() => hent(), [hent]);

  const updateOversigt = (oversigt: Oversigt) => {
    setLoad((current) =>
      current.status === "ready" ? { ...current, oversigt } : current,
    );
  };

  const flytTil = (step: Exclude<OnboardingStep, "afsluttet">) =>
    apiSend<{ state: Oversigt["state"] }>("PATCH", "/api/onboarding/step", {
      step,
    }).then((result) => {
      if (load.status === "ready") {
        updateOversigt({ ...load.oversigt, state: result.state });
      }
    });

  const flytMedFejl = (step: Exclude<OnboardingStep, "afsluttet">) => {
    void flytTil(step).catch(() =>
      toast.show("Kunne ikke skifte trin — prøv igen.", "error"),
    );
  };

  if (load.status === "loading") {
    return (
      <div className={styles.loading} aria-label="Henter opsætning">
        <Skeleton height="2rem" width="15rem" />
        <Skeleton height="4rem" />
        <Skeleton height="18rem" />
      </div>
    );
  }
  if (load.status === "failed") {
    return (
      <Card>
        <EmptyState
          title="Kunne ikke hente opsætningen"
          description="Tjek forbindelsen og prøv igen. Dine gemte valg er ikke ændret."
          cta={<Button onClick={hent}>Prøv igen</Button>}
        />
      </Card>
    );
  }
  if (load.status === "forbidden") {
    return (
      <Card>
        <EmptyState
          title="Kun administratorer kan åbne opsætningen"
          description="Du er logget ind som medarbejder. Gå tilbage til din egen tidsregistrering."
          cta={<Button onClick={() => navigate("/")}>Tilbage til appen</Button>}
        />
      </Card>
    );
  }

  const { oversigt, profiler, employees } = load;
  const step = oversigt.state.step === "regler" ? "arbejdsform" : oversigt.state.step;

  return (
    <div className={styles.page}>
      <header className={styles.intro}>
        <p className={styles.eyebrow}>Opsætning</p>
        <h1>Gør AppSmeden klar til jeres hverdag</h1>
        <p>
          Fire korte valg. Alt kan ændres igen senere uden at nulstille
          virksomheden.
        </p>
      </header>

      <Progress current={step} />

      {oversigt.state.afsluttet && step !== "afsluttet" && (
        <div className={styles.exitRow}>
          <Button variant="ghost" onClick={() => navigate("/")}>
            Tilbage til appen
          </Button>
        </div>
      )}

      {step === "virksomhed" && (
        <VirksomhedStep
          initialName={oversigt.konfiguration.firmanavn}
          onDone={(firmanavn) => {
            updateOversigt({
              state: {
                step: "arbejdsform",
                afsluttet: oversigt.state.afsluttet,
              },
              konfiguration: { ...oversigt.konfiguration, firmanavn },
            });
          }}
        />
      )}
      {step === "arbejdsform" && (
        <KonfigurationStep
          initial={oversigt.konfiguration}
          profiler={profiler}
          onBack={() => flytMedFejl("virksomhed")}
          onConfirmed={(result) => {
            notifyFeaturesUpdated();
            updateOversigt({
              state: result.state,
              konfiguration: result.konfiguration,
            });
          }}
        />
      )}
      {step === "medarbejder" && (
        <MedarbejderStep
          employees={employees}
          invite={invite}
          projectsEnabled={oversigt.konfiguration.projekterAktiv}
          onBack={() => flytMedFejl("arbejdsform")}
          onCreated={(info) => {
            setInvite(info);
            setLoad((current) =>
              current.status === "ready"
                ? {
                    ...current,
                    employees: [
                      ...current.employees,
                      { id: info.userId, navn: info.navn },
                    ],
                  }
                : current,
            );
          }}
          onCompleted={(state) =>
            updateOversigt({ ...oversigt, state })
          }
        />
      )}
      {step === "afsluttet" && (
        <AfsluttetStep
          config={oversigt.konfiguration}
          invite={invite}
          onDone={() => {
            navigate("/");
            window.scrollTo({ top: 0, left: 0 });
          }}
          onEdit={(target) => {
            flytMedFejl(target);
          }}
        />
      )}
    </div>
  );
}

function Progress({ current }: { current: OnboardingStep }) {
  const activeIndex = Math.max(0, STEP_ORDER.indexOf(current));
  return (
    <ol className={styles.progress} aria-label="Fremskridt i opsætningen">
      {STEP_ORDER.map((step, index) => (
        <li
          key={step}
          className={index <= activeIndex ? styles.progressActive : undefined}
          aria-current={step === current ? "step" : undefined}
        >
          <span>{index + 1}</span>
          {STEP_LABELS[step]}
        </li>
      ))}
    </ol>
  );
}

function VirksomhedStep({
  initialName,
  onDone,
}: {
  initialName: string;
  onDone: (name: string) => void;
}) {
  const [name, setName] = useState(initialName);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      setError("Angiv virksomhedens navn.");
      return;
    }
    setBusy(true);
    setError(null);
    const saveName =
      trimmed === initialName
        ? Promise.resolve()
        : apiSend("PATCH", "/api/settings", { firmanavn: trimmed });
    saveName
      .then(() =>
        apiSend("PATCH", "/api/onboarding/step", { step: "arbejdsform" }),
      )
      .then(() => onDone(trimmed))
      .catch(() => {
        setBusy(false);
        setError("Kunne ikke gemme — prøv igen.");
      });
  };

  return (
    <Card title="1. Tjek virksomheden">
      <form className={styles.form} onSubmit={submit}>
        <p className={styles.hint}>
          Navnet vises for medarbejderne og på rapporter.
        </p>
        <Field
          label="Virksomhedsnavn"
          value={name}
          onChange={(event) => setName(event.target.value)}
          {...(error === null ? {} : { error })}
          autoFocus
          required
        />
        <div className={styles.actions}>
          <Button type="submit" disabled={busy}>
            {busy ? "Gemmer…" : "Videre til arbejdsform"}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function KonfigurationStep({
  initial,
  profiler,
  onBack,
  onConfirmed,
}: {
  initial: Konfiguration;
  profiler: Profil[];
  onBack: () => void;
  onConfirmed: (result: Oversigt) => void;
}) {
  const [choice, setChoice] = useState<ProfilKey | "manuel" | null>(null);
  const [config, setConfig] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [manualValid, setManualValid] = useState(true);
  const previewRequest = useRef(0);

  const chooseProfile = (key: ProfilKey) => {
    const requestId = previewRequest.current + 1;
    previewRequest.current = requestId;
    setChoice(key);
    setBusy(true);
    setError(null);
    apiSend<Preview>("POST", "/api/onboarding/preview", {
      type: "profil",
      firmanavn: initial.firmanavn,
      profil: key,
    })
      .then((preview) => {
        if (previewRequest.current === requestId) {
          setConfig(preview.konfiguration);
        }
      })
      .catch(() => {
        if (previewRequest.current === requestId) {
          setError("Kunne ikke hente forslaget — prøv igen.");
        }
      })
      .finally(() => {
        if (previewRequest.current === requestId) setBusy(false);
      });
  };

  const chooseManual = () => {
    previewRequest.current += 1;
    setChoice("manuel");
    setConfig((current) => normaliserManuelMvp(current));
    setManualValid(false);
    setBusy(false);
    setError(null);
  };

  const confirm = () => {
    if (choice === null) {
      setError("Vælg en profil eller manuel opsætning.");
      return;
    }
    if (choice === "manuel" && !manualValid) {
      setError("Ret de markerede talfelter, før du fortsætter.");
      return;
    }
    setBusy(true);
    setError(null);
    const payload =
      choice === "manuel"
        ? { type: "manuel" as const, ...config }
        : {
            type: "profil" as const,
            firmanavn: config.firmanavn,
            profil: choice,
          };
    apiSend<Oversigt>("PUT", "/api/onboarding", payload)
      .then(onConfirmed)
      .catch(() => {
        setBusy(false);
        setError("Kunne ikke gemme opsætningen — prøv igen.");
      });
  };

  return (
    <Card title="2. Vælg arbejdsform og regler">
      <div className={styles.form}>
        <p className={styles.hint}>
          Vælg et forslag som start. Du ser altid de konkrete valg, før de
          gemmes.
        </p>
        <div className={styles.profileGrid} role="radiogroup" aria-label="Virksomhedsprofil">
          {profiler.map((profil) => (
            <button
              key={profil.key}
              type="button"
              role="radio"
              aria-checked={choice === profil.key}
              className={`${styles.profile} ${choice === profil.key ? styles.profileSelected : ""}`}
              onClick={() => chooseProfile(profil.key)}
            >
              <strong>{profil.navn}</strong>
              <span>{profil.beskrivelse}</span>
            </button>
          ))}
          <button
            type="button"
            role="radio"
            aria-checked={choice === "manuel"}
            className={`${styles.profile} ${choice === "manuel" ? styles.profileSelected : ""}`}
            onClick={chooseManual}
          >
            <strong>Manuel opsætning</strong>
            <span>Vælg selv registrering og de regler, der passer til den.</span>
          </button>
        </div>

        {choice !== null && (
          <div className={styles.preview} aria-live="polite">
            <h3>{choice === "manuel" ? "Jeres valg" : "Forslaget indeholder"}</h3>
            {choice === "manuel" ? (
              <ManualFields
                config={config}
                onChange={setConfig}
                onValidityChange={setManualValid}
              />
            ) : (
              <ConfigSummary config={config} />
            )}
            {config.pause.aktiv && (
              <p className={styles.legalNote}>
                Pauseforslaget er en produktstandard, ikke juridisk rådgivning.
                Det kan altid ændres.
              </p>
            )}
          </div>
        )}

        {error !== null && <p className={styles.error} role="alert">{error}</p>}
        <div className={styles.actions}>
          <Button variant="secondary" onClick={onBack} disabled={busy}>Tilbage</Button>
          <Button
            onClick={confirm}
            disabled={busy || choice === null || (choice === "manuel" && !manualValid)}
          >
            {busy ? "Arbejder…" : "Bekræft og fortsæt"}
          </Button>
        </div>
      </div>
    </Card>
  );
}

function ManualFields({
  config,
  onChange,
  onValidityChange,
}: {
  config: Konfiguration;
  onChange: (config: Konfiguration) => void;
  onValidityChange: (valid: boolean) => void;
}) {
  const [thresholdRaw, setThresholdRaw] = useState(
    String(config.pause.taerskelMinutter),
  );
  const [minutesRaw, setMinutesRaw] = useState(String(config.pause.minutter));
  const threshold = parseIntegerField(thresholdRaw, 1, 1440);
  const minutes = parseIntegerField(minutesRaw, 0, 480);
  const thresholdError = numberFieldError(thresholdRaw, threshold, 1, 1440);
  const minutesError = numberFieldError(minutesRaw, minutes, 0, 480);
  const fieldsValid = !config.pause.aktiv || (threshold !== null && minutes !== null);

  useEffect(() => onValidityChange(fieldsValid), [fieldsValid, onValidityChange]);

  const setMode = (mode: RegistreringsMode) => {
    onChange(normaliserManuelMvp({ ...config, registreringsMode: mode }));
  };

  return (
    <div className={styles.manualFields}>
      <fieldset className={styles.fieldset}>
        <legend>Registreringsform</legend>
        <label><input type="radio" name="mode" checked={config.registreringsMode === "stempel"} onChange={() => setMode("stempel")} /> Stempel ind og ud</label>
        <label><input type="radio" name="mode" checked={config.registreringsMode === "standard"} onChange={() => setMode("standard")} /> Fast standarduge</label>
      </fieldset>
      {config.registreringsMode === "standard" ? (
        <p className={styles.hint}>
          Fast standarduge er planlagt nettotid. Automatisk pause og projekter
          bruges kun ved ind- og udstempling.
        </p>
      ) : (
        <>
          <label className={styles.check}><input type="checkbox" checked={config.pause.aktiv} onChange={(event) => onChange({ ...config, pause: { ...config.pause, aktiv: event.target.checked } })} /> Automatisk pause</label>
          {config.pause.aktiv && (
            <div className={styles.numberGrid}>
              <Field
                label="Efter minutter"
                type="number"
                min="1"
                max="1440"
                step="1"
                value={thresholdRaw}
                onChange={(event) => {
                  const raw = event.target.value;
                  setThresholdRaw(raw);
                  const value = parseIntegerField(raw, 1, 1440);
                  if (value !== null) {
                    onChange({ ...config, pause: { ...config.pause, taerskelMinutter: value } });
                  }
                }}
                {...(thresholdError === null ? {} : { error: thresholdError })}
              />
              <Field
                label="Træk minutter"
                type="number"
                min="0"
                max="480"
                step="1"
                value={minutesRaw}
                onChange={(event) => {
                  const raw = event.target.value;
                  setMinutesRaw(raw);
                  const value = parseIntegerField(raw, 0, 480);
                  if (value !== null) {
                    onChange({ ...config, pause: { ...config.pause, minutter: value } });
                  }
                }}
                {...(minutesError === null ? {} : { error: minutesError })}
              />
            </div>
          )}
          <label className={styles.check}><input type="checkbox" checked={config.projekterAktiv} onChange={(event) => onChange({ ...config, projekterAktiv: event.target.checked })} /> Brug projekter og kunder</label>
        </>
      )}
      <label className={styles.check}><input type="checkbox" checked={config.features.koersel} onChange={(event) => onChange({ ...config, features: { ...config.features, koersel: event.target.checked } })} /> Brug kørselsregistrering</label>
    </div>
  );
}

function normaliserManuelMvp(config: Konfiguration): Konfiguration {
  const erStandard = config.registreringsMode === "standard";
  return {
    ...config,
    pause: { ...config.pause, aktiv: erStandard ? false : config.pause.aktiv },
    projekterAktiv: erStandard ? false : config.projekterAktiv,
    features: { clockin: true, ai: false, koersel: config.features.koersel },
  };
}

function parseIntegerField(raw: string, min: number, max: number): number | null {
  if (!/^\d+$/.test(raw)) return null;
  const value = Number(raw);
  return Number.isSafeInteger(value) && value >= min && value <= max ? value : null;
}

function numberFieldError(
  raw: string,
  value: number | null,
  min: number,
  max: number,
): string | null {
  if (value !== null) return null;
  return raw.length === 0
    ? "Angiv et antal minutter."
    : `Brug et helt tal fra ${min} til ${max}.`;
}

function ConfigSummary({ config }: { config: Konfiguration }) {
  return (
    <dl className={styles.summary}>
      <div><dt>Registrering</dt><dd>{config.registreringsMode === "stempel" ? "Stempel ind/ud" : "Fast standarduge"}</dd></div>
      {config.registreringsMode === "stempel" && (
        <>
          <div><dt>Pause</dt><dd>{config.pause.aktiv ? `${config.pause.minutter} min efter ${config.pause.taerskelMinutter} min` : "Ingen automatisk pause"}</dd></div>
          <div><dt>Projekter</dt><dd>{config.projekterAktiv ? "Til" : "Fra"}</dd></div>
        </>
      )}
      {config.features.koersel && <div><dt>Kørsel</dt><dd>Til</dd></div>}
    </dl>
  );
}

function MedarbejderStep({
  employees,
  invite,
  projectsEnabled,
  onBack,
  onCreated,
  onCompleted,
}: {
  employees: Employee[];
  invite: InviteInfo | null;
  projectsEnabled: boolean;
  onBack: () => void;
  onCreated: (invite: InviteInfo) => void;
  onCompleted: (state: Oversigt["state"]) => void;
}) {
  const [projectSetupDone, setProjectSetupDone] = useState(!projectsEnabled);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const finishProjectSetup = useCallback(() => setProjectSetupDone(true), []);

  const complete = () =>
    apiSend<{ state: Oversigt["state"] }>("POST", "/api/onboarding/complete")
      .then((result) => onCompleted(result.state));

  const create = (event: FormEvent) => {
    event.preventDefault();
    if (name.trim().length === 0) {
      setError("Angiv medarbejderens navn.");
      return;
    }
    setBusy(true);
    setError(null);
    apiSend<InviteInfo>("POST", "/api/employees", {
      navn: name.trim(),
      requestId,
    })
      .then((info) => {
        onCreated(info);
        setName("");
        setRequestId(crypto.randomUUID());
        // Afslutningen har sin egen fejlsti: medarbejderen er allerede
        // oprettet, hvis dette andet request fejler.
        void complete().catch(() => {
          setBusy(false);
          setError(
            "Medarbejderen er oprettet — opsætningen kunne ikke afsluttes. Prøv at afslutte igen.",
          );
        });
      })
      .catch((cause: unknown) => {
        setBusy(false);
        setError(
          cause instanceof ApiError && cause.status === 409
            ? cause.message
            : "Kunne ikke oprette medarbejderen — prøv igen.",
        );
      });
  };

  const finishExisting = () => {
    setBusy(true);
    setError(null);
    complete().catch(() => {
      setBusy(false);
      setError("Kunne ikke afslutte opsætningen — prøv igen.");
    });
  };

  if (!projectSetupDone) {
    return <FoersteProjektStep onBack={onBack} onDone={finishProjectSetup} />;
  }

  return (
    <Card title="3. Invitér den første medarbejder">
      <div className={styles.form}>
        <p className={styles.hint}>
          Medarbejderen får et engangslink og en QR-kode. Der skal ikke
          oprettes en adgangskode eller installeres en app.
        </p>
        {employees.length > 0 && (
          <p className={styles.existing}>
            {employees.length === 1
              ? `${employees[0]!.navn} er allerede oprettet.`
              : `${employees.length} medarbejdere er allerede oprettet.`}
          </p>
        )}
        {invite !== null && <InviteDetails invite={invite} />}
        {error !== null && <p className={styles.error} role="alert">{error}</p>}
        <form className={styles.form} onSubmit={create}>
          <Field
            label={employees.length === 0 ? "Medarbejderens navn" : "Ny medarbejders navn"}
            placeholder="Fx Jonas Jensen"
            value={name}
            onChange={(event) => setName(event.target.value)}
            autoFocus
            required
          />
          <div className={styles.actions}>
            <Button variant="secondary" onClick={onBack} disabled={busy}>Tilbage</Button>
            <Button type="submit" disabled={busy}>
              {busy ? "Opretter…" : employees.length === 0 ? "Opret og vis invitation" : "Tilføj medarbejder"}
            </Button>
            {employees.length > 0 && (
              <Button onClick={finishExisting} disabled={busy}>
                {busy ? "Afslutter…" : "Afslut opsætning"}
              </Button>
            )}
          </div>
        </form>
      </div>
    </Card>
  );
}

function FoersteProjektStep({
  onBack,
  onDone,
}: {
  onBack: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [status, setStatus] = useState<"loading" | "required" | "failed">("loading");
  const [navn, setNavn] = useState("");
  const [kundenavn, setKundenavn] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetchAttempt, setFetchAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const timeout = window.setTimeout(
      () => controller.abort(),
      PROJEKT_FETCH_TIMEOUT_MS,
    );
    setStatus("loading");
    setError(null);
    apiGet<Projekt[]>("/api/clockin/admin/projekter", {
      signal: controller.signal,
    })
      .then((projekter) => {
        if (!active) return;
        if (projekter.some((projekt) => projekt.aktiv)) {
          onDone();
          return;
        }
        setStatus("required");
      })
      .catch(() => {
        if (active) setStatus("failed");
      })
      .finally(() => window.clearTimeout(timeout));
    return () => {
      active = false;
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [fetchAttempt, onDone]);

  const create = (event: FormEvent) => {
    event.preventDefault();
    if (navn.trim().length === 0) {
      setError("Angiv et projektnavn.");
      return;
    }
    setBusy(true);
    setError(null);
    apiSend<Projekt>("POST", "/api/clockin/admin/projekter", {
      navn: navn.trim(),
      kundenavn: kundenavn.trim().length > 0 ? kundenavn.trim() : null,
    })
      .then(() => {
        toast.show("Projektet er oprettet. Invitér nu medarbejderen.", "success");
        onDone();
      })
      .catch((cause: unknown) => {
        setBusy(false);
        setError(
          cause instanceof ApiError && cause.status === 409
            ? "Der findes allerede et aktivt projekt med det navn."
            : "Kunne ikke oprette projektet — prøv igen.",
        );
      });
  };

  return (
    <Card title="3. Opret det første projekt">
      {status === "loading" && (
        <div className={styles.loading} aria-label="Henter projekter">
          <Skeleton height="1.25rem" width="18rem" />
          <Skeleton height="7rem" />
          <div className={styles.actions}>
            <Button variant="secondary" onClick={onBack}>Tilbage</Button>
            <Button variant="ghost" onClick={onDone}>Fortsæt uden projekt</Button>
          </div>
        </div>
      )}
      {status === "failed" && (
        <EmptyState
          title="Kunne ikke hente projekterne"
          description="Prøv igen, eller fortsæt uden et projekt og opret det senere under Indstillinger."
          cta={(
            <div className={styles.actions}>
              <Button variant="secondary" onClick={onBack}>Tilbage</Button>
              <Button variant="secondary" onClick={onDone}>Fortsæt uden projekt</Button>
              <Button onClick={() => setFetchAttempt((attempt) => attempt + 1)}>
                Prøv igen
              </Button>
            </div>
          )}
        />
      )}
      {status === "required" && (
        <form className={styles.form} onSubmit={create}>
          <p className={styles.hint}>
            Medarbejderen vælger projekt ved indstempling. Opret det første nu,
            så den første vagt kan blive registreret på det rigtige arbejde.
          </p>
          {error !== null && <p className={styles.error} role="alert">{error}</p>}
          <Field
            label="Projektnavn"
            placeholder="Fx Tagrenovering"
            value={navn}
            onChange={(event) => setNavn(event.target.value)}
            autoFocus
            required
          />
          <Field
            label="Kunde (valgfri)"
            placeholder="Fx Fjordbo"
            value={kundenavn}
            onChange={(event) => setKundenavn(event.target.value)}
          />
          <div className={styles.actions}>
            <Button variant="secondary" onClick={onBack} disabled={busy}>Tilbage</Button>
            <Button variant="ghost" onClick={onDone} disabled={busy}>Fortsæt uden projekt</Button>
            <Button type="submit" disabled={busy}>
              {busy ? "Opretter…" : "Opret projekt og fortsæt"}
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}

function InviteDetails({ invite }: { invite: InviteInfo }) {
  const toast = useToast();
  const copy = () => {
    if (navigator.clipboard === undefined) {
      toast.show("Markér linket og kopiér det manuelt.", "error");
      return;
    }
    navigator.clipboard
      .writeText(invite.inviteUrl)
      .then(() => toast.show("Link kopieret.", "success"))
      .catch(() => toast.show("Kunne ikke kopiere linket.", "error"));
  };

  return (
    <div className={styles.invite}>
      <img src={invite.qrDataUrl} alt={`QR-kode med invitationslink til ${invite.navn}`} />
      <div className={styles.linkRow}>
        <input readOnly value={invite.inviteUrl} aria-label="Invitationslink" onFocus={(event) => event.target.select()} />
        <Button variant="secondary" onClick={copy}>Kopiér</Button>
      </div>
      <p className={styles.hint}>
        Linket udløber {new Date(invite.udloeberAt).toLocaleDateString("da-DK")}.
      </p>
    </div>
  );
}

function AfsluttetStep({
  config,
  invite,
  onDone,
  onEdit,
}: {
  config: Konfiguration;
  invite: InviteInfo | null;
  onDone: () => void;
  onEdit: (step: "virksomhed" | "arbejdsform" | "medarbejder") => void;
}) {
  return (
    <Card title={invite === null ? "Opsætningen er klar" : `${invite.navn} er inviteret`}>
      <div className={styles.finished}>
        <div className={styles.successMark} aria-hidden="true">✓</div>
        <p>
          {invite === null
            ? "AppSmeden er sat op. Du kan ændre alle virksomhedsvalg igen her."
            : "Scan QR-koden på medarbejderens telefon, eller send linket."}
        </p>
        {invite !== null && <InviteDetails invite={invite} />}
        <ConfigSummary config={config} />
        <Button onClick={onDone}>Gå til overblik</Button>
        <div className={styles.editActions}>
          <Button variant="secondary" onClick={() => onEdit("virksomhed")}>Ret virksomhedsnavn</Button>
          <Button variant="secondary" onClick={() => onEdit("arbejdsform")}>Ret arbejdsform og regler</Button>
          <Button variant="secondary" onClick={() => onEdit("medarbejder")}>Tilføj medarbejder</Button>
        </div>
      </div>
    </Card>
  );
}
